import React, { useState, useEffect, useCallback } from 'react';
import { redirectService } from '../services/redirectService';
import './RedirectManager.css';
import Modal from './Modal';
import ActionMenu from './ActionMenu';

// RedirectForm component for adding/editing redirects
const RedirectForm = ({ redirect, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    subdomain: '',
    destinationUrl: '',
    active: true,
    ...(redirect || {}),
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Basic validation
      if (!formData.subdomain) {
        throw new Error('Subdomain is required');
      }
      if (!formData.destinationUrl) {
        throw new Error('Destination URL is required');
      }

      // Save the redirect
      await onSave(formData);
    } catch (err) {
      setError(err.error || err.message || 'Failed to save redirect');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="redirect-form" onSubmit={handleSubmit}>
      <h3>{redirect ? 'Edit Redirect' : 'Add New Redirect'}</h3>

      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label htmlFor="subdomain">Subdomain</label>
        <div className="input-with-addon">
          <input
            type="text"
            id="subdomain"
            name="subdomain"
            value={formData.subdomain}
            onChange={handleChange}
            placeholder="example"
            required
          />
          <span className="input-addon">.getsauce.com</span>
        </div>
        <small>The subdomain to redirect (e.g., 'example' will redirect both example.getsauce.com and www.example.getsauce.com)</small>
      </div>

      <div className="form-group">
        <label htmlFor="destinationUrl">Destination URL</label>
        <input
          type="url"
          id="destinationUrl"
          name="destinationUrl"
          value={formData.destinationUrl}
          onChange={handleChange}
          placeholder="https://destination.com/page"
          required
        />
        <small>The full URL where users will be redirected to. Query parameters will be preserved.</small>
      </div>

      <div className="form-group checkbox">
        <input
          type="checkbox"
          id="active"
          name="active"
          checked={formData.active}
          onChange={handleChange}
        />
        <label htmlFor="active">Active</label>
      </div>

      <div className="form-actions">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="form-button cancel-button"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="form-button save-button"
        >
          {isSubmitting ? 'Saving...' : 'Save Redirect'}
        </button>
      </div>
    </form>
  );
};

// Main CombinedRedirectManager component
const CombinedRedirectManager = () => {
  // State variables from both components
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRedirect, setEditingRedirect] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const [filterType, setFilterType] = useState('all');
  const [validationFilter, setValidationFilter] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [selectedRedirectIds, setSelectedRedirectIds] = useState([]);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('info');
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalConfirmAction, setModalConfirmAction] = useState(null);
  const [modalConfirmText, setModalConfirmText] = useState('OK');
  const [modalCancelText, setModalCancelText] = useState('');

  // Fetch redirects with useCallback to avoid infinite loops
  const fetchRedirects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await redirectService.getAllRedirects();
      setRedirects(data);
      setError('');
    } catch (err) {
      setError('Failed to load redirects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRedirects();
  }, [fetchRedirects]);

  // Helper functions for modals
  const showInfoModal = (title, message) => {
    setModalType('info');
    setModalTitle(title);
    setModalMessage(message);
    setModalConfirmAction(null);
    setModalConfirmText('OK');
    setModalCancelText('');
    setModalOpen(true);
  };

  const showErrorModal = (title, message) => {
    setModalType('error');
    setModalTitle(title);
    setModalMessage(message);
    setModalConfirmAction(null);
    setModalConfirmText('OK');
    setModalCancelText('');
    setModalOpen(true);
  };

  const showConfirmModal = (title, message, confirmAction, confirmText = 'Confirm', cancelText = 'Cancel', type = 'warning') => {
    setModalType(type);
    setModalTitle(title);
    setModalMessage(message);
    setModalConfirmAction(() => confirmAction);
    setModalConfirmText(confirmText);
    setModalCancelText(cancelText);
    setModalOpen(true);
  };

  // Functions from RedirectManager
  const handleSaveRedirect = async (redirectData) => {
    try {
      if (editingRedirect) {
        // Update existing redirect
        await redirectService.updateRedirect(editingRedirect._id, redirectData);
      } else {
        // Create new redirect
        await redirectService.createRedirect(redirectData);
      }

      // Refresh the list
      fetchRedirects();

      // Close form
      setEditingRedirect(null);
      setIsAddingNew(false);
    } catch (err) {
      console.error('Error saving redirect:', err);
      throw err;
    }
  };

  const handleDeleteRedirect = async (id) => {
    showConfirmModal(
      'Delete Redirect',
      'Are you sure you want to delete this redirect?',
      async () => {
        try {
          await redirectService.deleteRedirect(id);
          fetchRedirects();
          showInfoModal('Success', 'Redirect deleted successfully');
        } catch (err) {
          setError('Failed to delete redirect');
          showErrorModal('Error', 'Failed to delete redirect');
          console.error(err);
        }
      },
      'Delete',
      'Cancel',
      'error'
    );
  };

  const handleCancelEdit = () => {
    setEditingRedirect(null);
    setIsAddingNew(false);
  };

  // Validation functions from ValidatorManager
  const handleValidateRedirect = async (id) => {
    setLoading(true);
    try {
      // Find the redirect to get its subdomain for the message
      const redirect = redirects.find(r => r._id === id);
      const subdomain = redirect ? redirect.subdomain : 'unknown';

      console.log(`Starting validation for redirect ID: ${id}, subdomain: ${subdomain}`);

      const result = await redirectService.validateRedirect(id);
      console.log(`Validation completed for ${subdomain}`, result);

      // Fetch the updated redirect from the server to ensure we have the latest data
      const updatedRedirect = await redirectService.getRedirect(id);

      // Find the redirect in the current list and update it with the fetched data
      const updatedRedirects = redirects.map(redirect => 
        redirect._id === id ? updatedRedirect : redirect
      );
      setRedirects(updatedRedirects);

      // Show a success message
      showInfoModal(
        'Validation Complete', 
        `Validation for ${result.subdomain} completed. Result: ${result.valid ? 'VALID' : 'INVALID'}`
      );
    } catch (error) {
      console.error(`Validation error:`, error);
      showInfoModal('Validation Error', `Error validating redirect: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFixRedirect = async (id) => {
    setLoading(true);
    try {
      // Find the redirect to get its subdomain for the message
      const redirect = redirects.find(r => r._id === id);
      const subdomain = redirect ? redirect.subdomain : 'unknown';

      console.log(`Starting fix for redirect ID: ${id}, subdomain: ${subdomain}`);

      const result = await redirectService.fixRedirect(id);
      console.log(`Fix completed for ${subdomain}`, result);

      // Fetch the updated redirect from the server to ensure we have the latest data
      const updatedRedirect = await redirectService.getRedirect(id);

      // Find the redirect in the current list and update it with the fetched data
      const updatedRedirects = redirects.map(redirect => 
        redirect._id === id ? updatedRedirect : redirect
      );
      setRedirects(updatedRedirects);

      // Show a success message
      if (result.fixed) {
        showInfoModal(
          'Fix Complete', 
          `Fix for ${result.subdomain} completed successfully. Please check validation status.`
        );
      } else {
        showInfoModal(
          'Fix Complete', 
          `Fix for ${result.subdomain} completed, but some issues could not be fixed automatically. Please check validation status.`
        );
      }
    } catch (error) {
      console.error(`Fix error:`, error);
      showInfoModal('Fix Error', `Error fixing redirect: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if a redirect has fixable issues
  const isRedirectFixable = (redirect) => {
    // If the redirect is valid or not validated, it's not fixable
    if (!redirect.lastValidation || redirect.lastValidation.valid) {
      return false;
    }

    // If the redirect has validation details, check if it has fixable issues
    const { details } = redirect.lastValidation;
    
    // Check if any of the fixable issues (DNS or KV) are invalid
    const hasFixableIssues = 
      (details.googleDns && !details.googleDns.googleDnsValid) ||
      (details.acmeChallenge && !details.acmeChallenge.acmeChallengeValid) ||
      (details.cloudflareKv && !details.cloudflareKv.cloudflareKvValid) ||
      (details.cloudflareDns && !details.cloudflareDns.cloudflareDnsValid);
    
    // Check if the only issue is URL redirection (unfixable)
    const onlyHasUrlRedirectionIssue = 
      (!details.googleDns || details.googleDns.googleDnsValid) &&
      (!details.acmeChallenge || details.acmeChallenge.acmeChallengeValid) &&
      (!details.cloudflareKv || details.cloudflareKv.cloudflareKvValid) &&
      (!details.cloudflareDns || details.cloudflareDns.cloudflareDnsValid) &&
      (details.urlRedirection && !details.urlRedirection.urlRedirectionValid);
    
    // Return true if there are fixable issues and it's not just a URL redirection issue
    return hasFixableIssues && !onlyHasUrlRedirectionIssue;
  };

  // Bulk validation functions
  const handleValidateSelected = async () => {
    if (selectedRedirectIds.length === 0) {
      showInfoModal('No Selection', 'No redirects selected for validation.');
      return;
    }

    setLoading(true);
    try {
      console.log(`Starting validation for ${selectedRedirectIds.length} selected redirects`);

      const results = await redirectService.validateMultipleRedirects(selectedRedirectIds);
      console.log(`Validation completed for ${results.length} redirects`, results);

      // Fetch all redirects again to get the latest data
      const updatedData = await redirectService.getAllRedirects();

      // Update the state with the fresh data
      setRedirects(updatedData);

      // Count valid and invalid results
      const validCount = results.filter(r => r.valid).length;
      const invalidCount = results.filter(r => r.valid === false).length;
      const errorCount = results.filter(r => r.error).length;

      // Show a summary message
      showInfoModal(
        'Validation Complete', 
        `Validation of ${results.length} redirects completed.\n\nValid: ${validCount}\nInvalid: ${invalidCount}\nErrors: ${errorCount}`
      );
    } catch (error) {
      console.error(`Validation error:`, error);
      showInfoModal('Validation Error', `Error validating redirects: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFixSelected = async () => {
    if (selectedRedirectIds.length === 0) {
      showInfoModal('No Selection', 'No redirects selected for fixing.');
      return;
    }

    // Check if any of the selected redirects are fixable
    const selectedRedirects = redirects.filter(r => selectedRedirectIds.includes(r._id));
    const fixableRedirects = selectedRedirects.filter(r => isRedirectFixable(r));
    
    if (fixableRedirects.length === 0) {
      showInfoModal('No Fixable Redirects', 'None of the selected redirects have fixable issues.');
      return;
    }

    // Only fix the redirects that are fixable
    const fixableIds = fixableRedirects.map(r => r._id);

    setLoading(true);
    try {
      console.log(`Starting fix for ${fixableIds.length} selected redirects`);

      const results = await redirectService.fixMultipleRedirects(fixableIds);
      console.log(`Fix completed for ${results.results.length} redirects`, results);

      // Fetch all redirects again to get the latest data
      const updatedData = await redirectService.getAllRedirects();

      // Update the state with the fresh data
      setRedirects(updatedData);

      // Show a summary message
      showInfoModal(
        'Fix Complete', 
        `Fix of ${results.results.length} redirects completed.\n\nFixed: ${results.fixedCount}\nFailed: ${results.failedCount}`
      );
    } catch (error) {
      console.error(`Fix error:`, error);
      showInfoModal('Fix Error', `Error fixing redirects: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateAll = async () => {
    showConfirmModal(
      'Validate All Redirects',
      'Validating all records will take a lot of time, maybe you should select specific records to validate instead. If you still want to do it, click on Confirm',
      async () => {
        setLoading(true);
        try {
          console.log('Starting validation for all redirects');

          const results = await redirectService.validateAllRedirects();
          console.log(`Validation completed for ${results.length} redirects`, results);

          // Fetch all redirects again to get the latest data
          const updatedData = await redirectService.getAllRedirects();

          // Update the state with the fresh data
          setRedirects(updatedData);

          // Count valid and invalid results
          const validCount = results.filter(r => r.valid).length;
          const invalidCount = results.filter(r => r.valid === false).length;
          const errorCount = results.filter(r => r.error).length;

          // Show a summary message
          showInfoModal(
            'Validation Complete', 
            `Validation of all ${results.length} redirects completed.\n\nValid: ${validCount}\nInvalid: ${invalidCount}\nErrors: ${errorCount}`
          );
        } catch (error) {
          console.error(`Validation error:`, error);
          showInfoModal('Validation Error', `Error validating all redirects: ${error.message || 'Unknown error'}`);
        } finally {
          setLoading(false);
        }
      },
      'Validate All',
      'Cancel',
      'warning'
    );
  };
  
  // Helper functions for table display
  const handleSelectRow = (id) => {
    setSelectedRedirectIds(prevSelectedIds =>
      prevSelectedIds.includes(id)
        ? prevSelectedIds.filter(selectedId => selectedId !== id)
        : [...prevSelectedIds, id]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all IDs from the currently visible records (currentRecords)
      const allRecordIdsOnPage = currentRecords.map(redirect => redirect._id);
      setSelectedRedirectIds(allRecordIdsOnPage);
    } else {
      setSelectedRedirectIds([]);
    }
  };

  // Determine if all current records on the page are selected
  const areAllCurrentRecordsSelected = () => {
    if (currentRecords.length === 0) return false;
    return currentRecords.every(redirect => selectedRedirectIds.includes(redirect._id));
  };

  // Helper function for sorting
  const sortRedirects = (a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle special cases for sorting
    if (sortField === 'subdomain') {
      aValue = a.subdomain.toLowerCase();
      bValue = b.subdomain.toLowerCase();
    }

    // Handle date fields
    if (sortField === 'createdAt' || sortField === 'updatedAt' || sortField === 'lastValidatedAt') {
      aValue = new Date(aValue || 0).getTime();
      bValue = new Date(bValue || 0).getTime();
    }

    // Handle validation status
    if (sortField === 'validationStatus') {
      // Define order: Valid, Invalid, Error, Not Validated
      const statusOrder = {
        'valid': 1,
        'invalid': 2,
        'error': 3,
        'not-validated': 4
      };

      const getStatus = (redirect) => {
        if (!redirect.lastValidation) return 'not-validated';
        if (redirect.lastValidation.error) return 'error';
        return redirect.lastValidation.valid ? 'valid' : 'invalid';
      };

      aValue = statusOrder[getStatus(a)] || 4;
      bValue = statusOrder[getStatus(b)] || 4;
    }

    // Handle null values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Compare values based on sort direction
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  };

  // Handle sort column click
  const handleSortClick = (field) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending for dates, ascending for others
      setSortField(field);
      if (field === 'createdAt' || field === 'updatedAt' || field === 'lastValidatedAt') {
        setSortDirection('desc');
      } else {
        setSortDirection('asc');
      }
    }
  };

  // CSV upload functions
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setUploadStatus(''); // Clear previous status
  };

  const normalizeSubdomain = (original) => {
    if (!original) return '';
    let subdomain = original.toLowerCase();
    // Remove protocol
    subdomain = subdomain.replace(/^https?:\/\//, '');
    // Remove .getsauce.com and anything after it (like path)
    subdomain = subdomain.split('.getsauce.com')[0];
    // Remove www. prefix
    subdomain = subdomain.replace(/^www\./, '');
    // Remove any remaining path if .getsauce.com was not present but a path was
    subdomain = subdomain.split('/')[0];
    // Final cleanup for any internal dots
    subdomain = subdomain.replace(/\./g, '');
    return subdomain;
  };

  const handleCsvUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Please select a CSV file first.');
      return;
    }

    setLoading(true);
    setUploadStatus('Processing CSV...');

    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvData = event.target.result;
      const lines = csvData.split(/\r\n|\n/);
      const redirectsToCreate = [];
      const errors = [];

      // Skip header row - uncomment if your CSVs have a header
      // if (lines.length > 0) lines.shift();

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines

        const parts = line.split(','); // Simple CSV split
        if (parts.length < 2) {
          errors.push(`Row ${i + 1}: Invalid format - expected 2 columns (subdomain,destinationUrl). Line: '${line}'`);
          continue;
        }

        const originalSubdomain = parts[0].trim();
        // Join remaining parts for destination URL in case it contains commas
        const destinationUrl = parts.slice(1).join(',').trim();

        if (!originalSubdomain || !destinationUrl) {
          errors.push(`Row ${i + 1}: Missing subdomain or destination URL. Line: '${line}'`);
          continue;
        }

        const normalizedSub = normalizeSubdomain(originalSubdomain);
        if (!normalizedSub) {
          errors.push(`Row ${i + 1}: Could not normalize subdomain from '${originalSubdomain}'. Line: '${line}'`);
          continue;
        }

        redirectsToCreate.push({ subdomain: normalizedSub, destinationUrl });
      }

      // Call the bulk create service instead of individual creations
      if (redirectsToCreate.length > 0) {
        try {
          const response = await redirectService.bulkCreateRedirects(redirectsToCreate);
          setUploadStatus(response.message || `${response.createdCount} redirects created, ${response.existingCount || 0} existing redirects rechecked, ${response.updatedCount || 0} records had destination URLs updated. ${response.errorCount} errors.`);
          if (response.errors && response.errors.length > 0) {
            console.error('CSV Bulk Upload Errors (from server):', response.errors);
          }
        } catch (err) {
          const errorMessage = err.response && err.response.data && err.response.data.error
                               ? err.response.data.error
                               : (err.message || 'Bulk creation failed');
          setUploadStatus(`Error: ${errorMessage}`);
          console.error('CSV Bulk Upload General Error:', err);
        }
      } else if (errors.length > 0) {
        // Only parsing errors occurred, no valid redirects to send
        setUploadStatus(`CSV processing failed. ${errors.length} parsing errors. Check console for details.`);
        console.error('CSV Upload Errors (Parsing):', errors);
      } else {
        setUploadStatus('No valid redirects found in CSV to process.');
      }

      setSelectedFile(null);
      if (document.querySelector('.csv-input')) { // Reset file input
        document.querySelector('.csv-input').value = '';
      }
      fetchRedirects(); // Refresh the list
      setLoading(false);
      setShowCsvUpload(false); // Close CSV upload section after processing
    };

    reader.onerror = () => {
      setUploadStatus('Error reading file.');
      setLoading(false);
      setShowCsvUpload(false); // Close CSV upload section on error too
    };

    reader.readAsText(selectedFile);
  };

  // Bulk actions from RedirectManager
  const handleBulkActivate = async () => {
    if (selectedRedirectIds.length === 0) {
      showInfoModal('No Selection', 'No redirects selected for activation.');
      return;
    }

    showConfirmModal(
      'Activate Redirects',
      `Are you sure you want to activate ${selectedRedirectIds.length} selected redirect(s)?`,
      async () => {
        setLoading(true);
        try {
          const response = await redirectService.bulkActivateRedirects(selectedRedirectIds);
          showInfoModal('Success', response.message || 'Bulk activation request processed.');
          fetchRedirects();
          setSelectedRedirectIds([]); // Clear selection
        } catch (err) {
          const errorMessage = err.error || 'Failed to activate selected redirects.';
          showErrorModal('Error', errorMessage);
          console.error('Bulk Activate Error:', err);
        }
        setLoading(false);
      },
      'Activate',
      'Cancel',
      'info'
    );
  };

  const handleBulkDeactivate = async () => {
    if (selectedRedirectIds.length === 0) {
      showInfoModal('No Selection', 'No redirects selected for deactivation.');
      return;
    }

    showConfirmModal(
      'Deactivate Redirects',
      `Are you sure you want to deactivate ${selectedRedirectIds.length} selected redirect(s)?`,
      async () => {
        setLoading(true);
        try {
          const response = await redirectService.bulkDeactivateRedirects(selectedRedirectIds);
          showInfoModal('Success', response.message || 'Bulk deactivation request processed.');
          fetchRedirects();
          setSelectedRedirectIds([]); // Clear selection
        } catch (err) {
          const errorMessage = err.error || 'Failed to deactivate selected redirects.';
          showErrorModal('Error', errorMessage);
          console.error('Bulk Deactivate Error:', err);
        }
        setLoading(false);
      },
      'Deactivate',
      'Cancel',
      'warning'
    );
  };

  const handleBulkDelete = async () => {
    if (selectedRedirectIds.length === 0) {
      showInfoModal('No Selection', 'No redirects selected for deletion.');
      return;
    }

    showConfirmModal(
      'Delete Redirects',
      `Are you sure you want to PERMANENTLY DELETE ${selectedRedirectIds.length} selected redirect(s)? This action cannot be undone.`,
      async () => {
        setLoading(true);
        try {
          const response = await redirectService.bulkDeleteRedirects(selectedRedirectIds);
          showInfoModal('Success', response.message || 'Bulk delete request processed.');
          fetchRedirects();
          setSelectedRedirectIds([]); // Clear selection
        } catch (err) {
          const errorMessage = err.error || 'Failed to delete selected redirects.';
          showErrorModal('Error', errorMessage);
          console.error('Bulk Delete Error:', err);
        }
        setLoading(false);
      },
      'Delete',
      'Cancel',
      'error'
    );
  };

  // Filter redirects based on search term, type, and validation status
  const filteredRedirects = redirects
    .filter(redirect => {
      const term = searchTerm.toLowerCase();
      return (
        redirect.subdomain.toLowerCase().includes(term) ||
        redirect.destinationUrl.toLowerCase().includes(term) ||
        (redirect.createdBy && redirect.createdBy.toLowerCase().includes(term))
      );
    })
    .filter(redirect => {
      if (filterType === 'all') {
        return true;
      }
      return redirect.redirectType === filterType;
    })
    .filter(redirect => {
      if (validationFilter === 'all') {
        return true;
      }
      if (validationFilter === 'valid') {
        return redirect.lastValidation && redirect.lastValidation.valid === true;
      }
      if (validationFilter === 'invalid') {
        // Include both records where valid is false and records with errors
        if (!redirect.lastValidation) return false;
        return redirect.lastValidation.valid === false || redirect.lastValidation.error;
      }
      if (validationFilter === 'not-validated') {
        return !redirect.lastValidation;
      }
      return true;
    })
    .sort(sortRedirects);

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRedirects.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRedirects.length / recordsPerPage);

  const paginate = pageNumber => setCurrentPage(pageNumber);

  const getRedirectTypeSymbol = (type) => {
    switch (type) {
      case 'storefront':
        return 'SF';
      case 'wordpress':
        return 'WP';
      case 'custom':
        return 'C';
      default:
        return '?'; // For unknown types
    }
  };

  // Function to show DNS and KV validation details in a modal
  const showDnsKvValidationModal = (redirect) => {
    if (!redirect.lastValidation) {
      showInfoModal('Validation Status', 'This redirect has not been validated yet.');
      return;
    }

    const { lastValidation } = redirect;

    // Format the timestamp
    const timestamp = lastValidation.timestamp 
      ? new Date(lastValidation.timestamp).toLocaleString() 
      : 'Unknown';

    // Create a formatted message with DNS and KV validation details
    let message = `Last validated: ${timestamp}\n\n`;

    if (lastValidation.error) {
      message += `Error: ${lastValidation.error}\n`;
      showInfoModal('Validation Error', message);
      return;
    }

    const details = lastValidation.details || {};

    // Add status for DNS and KV checks
    message += 'DNS & KV Validation Checks:\n\n';

    // Google DNS check
    if (details.googleDns) {
      message += `Google DNS: ${details.googleDns.googleDnsValid ? '✅ Passed' : '❌ Failed'}\n`;
      if (!details.googleDns.googleDnsValid && details.googleDns.details) {
        const gDetails = details.googleDns.details;
        if (gDetails.subdomain) {
          message += `  - Subdomain: ${gDetails.subdomain.valid ? 'Valid' : 'Invalid'}\n`;
        }
        if (gDetails.wwwSubdomain) {
          message += `  - WWW Subdomain: ${gDetails.wwwSubdomain.valid ? 'Valid' : 'Invalid'}\n`;
        }
      }
    }

    // ACME Challenge check
    if (details.acmeChallenge) {
      message += `ACME Challenge: ${details.acmeChallenge.acmeChallengeValid ? '✅ Passed' : '❌ Failed'}\n`;
    }

    // Cloudflare KV check
    if (details.cloudflareKv) {
      message += `Cloudflare KV: ${details.cloudflareKv.cloudflareKvValid ? '✅ Passed' : '❌ Failed'}\n`;
      if (!details.cloudflareKv.cloudflareKvValid && details.cloudflareKv.details) {
        const kvDetails = details.cloudflareKv.details;
        message += `  - Expected: ${kvDetails.expected}\n`;
        message += `  - Actual: ${kvDetails.actual || 'Not found'}\n`;
      }
    }

    // Cloudflare DNS check
    if (details.cloudflareDns) {
      message += `Cloudflare DNS: ${details.cloudflareDns.cloudflareDnsValid ? '✅ Passed' : '❌ Failed'}\n`;
      if (!details.cloudflareDns.cloudflareDnsValid && details.cloudflareDns.details) {
        const dnsDetails = details.cloudflareDns.details;
        if (dnsDetails.expected) {
          message += `  - Expected: Type=${dnsDetails.expected.type}, Content=${dnsDetails.expected.content}, Proxied=${dnsDetails.expected.proxied}\n`;
        }
        if (dnsDetails.actual) {
          message += `  - Actual: Type=${dnsDetails.actual.type}, Content=${dnsDetails.actual.content}, Proxied=${dnsDetails.actual.proxied}\n`;
        } else {
          message += `  - Actual: Record not found\n`;
        }
      }
    }

    // DNS/KV overall status
    const dnsKvValid = 
      (!details.googleDns || details.googleDns.googleDnsValid) && 
      (!details.acmeChallenge || details.acmeChallenge.acmeChallengeValid) && 
      (!details.cloudflareKv || details.cloudflareKv.cloudflareKvValid) && 
      (!details.cloudflareDns || details.cloudflareDns.cloudflareDnsValid);
    
    message += `\nDNS & KV Status: ${dnsKvValid ? '✅ VALID' : '❌ INVALID'}`;

    showInfoModal('DNS & KV Validation Details', message);
  };

  // Function to show URL redirection validation details in a modal with enhanced error information
  const showUrlValidationModal = (redirect) => {
    if (!redirect.lastValidation) {
      showInfoModal('Validation Status', 'This redirect has not been validated yet.');
      return;
    }

    const { lastValidation } = redirect;

    // Format the timestamp
    const timestamp = lastValidation.timestamp 
      ? new Date(lastValidation.timestamp).toLocaleString() 
      : 'Unknown';

    // Create a formatted message with URL validation details
    let message = `Last validated: ${timestamp}\n\n`;

    if (lastValidation.error) {
      message += `Error: ${lastValidation.error}\n`;
      showInfoModal('Validation Error', message);
      return;
    }

    const details = lastValidation.details || {};

    // URL Redirection check with enhanced error information
    if (details.urlRedirection) {
      message += `URL Redirection: ${details.urlRedirection.urlRedirectionValid ? '✅ Passed' : '❌ Failed'}\n\n`;
      
      if (!details.urlRedirection.urlRedirectionValid && details.urlRedirection.details) {
        const urlDetails = details.urlRedirection.details;
        
        // Process subdomain URL validation
        if (urlDetails.subdomain) {
          message += `Subdomain URL (${urlDetails.subdomain.url}):\n`;
          
          if (urlDetails.subdomain.valid) {
            message += `  ✅ Valid - Redirects correctly\n`;
          } else {
            if (urlDetails.subdomain.error) {
              // Handle different types of errors
              if (urlDetails.subdomain.error.includes('certificate')) {
                message += `  ❌ SSL Certificate Error: ${urlDetails.subdomain.error}\n`;
              } else if (urlDetails.subdomain.error.includes('ENOTFOUND') || urlDetails.subdomain.error.includes('ETIMEDOUT')) {
                message += `  ❌ DNS Resolution Error: ${urlDetails.subdomain.error}\n`;
              } else {
                message += `  ❌ Error: ${urlDetails.subdomain.error}\n`;
              }
            }
            
            // Handle HTTP status code errors (can occur alongside URL mismatch)
            if (urlDetails.subdomain.statusCode && urlDetails.subdomain.statusCode !== 200) {
              message += `  ❌ HTTP Error: ${urlDetails.subdomain.statusCode} - ${getHttpStatusDescription(urlDetails.subdomain.statusCode)}\n`;
            }
            
            // Handle URL mismatch (can occur alongside HTTP errors)
            if (urlDetails.subdomain.finalUrl && urlDetails.subdomain.finalUrl !== redirect.destinationUrl) {
              message += `  ❌ URL Mismatch:\n`;
              message += `    Expected: ${redirect.destinationUrl}\n`;
              message += `    Actual: ${urlDetails.subdomain.finalUrl}\n`;
            }
          }
        }
        
        // Process www URL validation
        if (urlDetails.www) {
          message += `\nWWW URL (${urlDetails.www.url}):\n`;
          
          if (urlDetails.www.valid) {
            message += `  ✅ Valid - Redirects correctly\n`;
          } else {
            if (urlDetails.www.error) {
              // Handle different types of errors
              if (urlDetails.www.error.includes('certificate')) {
                message += `  ❌ SSL Certificate Error: ${urlDetails.www.error}\n`;
              } else if (urlDetails.www.error.includes('ENOTFOUND') || urlDetails.www.error.includes('ETIMEDOUT')) {
                message += `  ❌ DNS Resolution Error: ${urlDetails.www.error}\n`;
              } else {
                message += `  ❌ Error: ${urlDetails.www.error}\n`;
              }
            }
            
            // Handle HTTP status code errors (can occur alongside URL mismatch)
            if (urlDetails.www.statusCode && urlDetails.www.statusCode !== 200) {
              message += `  ❌ HTTP Error: ${urlDetails.www.statusCode} - ${getHttpStatusDescription(urlDetails.www.statusCode)}\n`;
            }
            
            // Handle URL mismatch (can occur alongside HTTP errors)
            if (urlDetails.www.finalUrl && urlDetails.www.finalUrl !== redirect.destinationUrl) {
              message += `  ❌ URL Mismatch:\n`;
              message += `    Expected: ${redirect.destinationUrl}\n`;
              message += `    Actual: ${urlDetails.www.finalUrl}\n`;
            }
          }
        }
      }
    } else {
      message += 'URL redirection has not been validated.';
    }

    showInfoModal('URL Redirection Validation Details', message);
  };

  // Helper function to get HTTP status code descriptions
  const getHttpStatusDescription = (statusCode) => {
    const statusCodes = {
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      410: 'Gone - Maybe Churned',
      500: 'Internal Server Error',
      502: 'Bad Gateway',
      503: 'Service Unavailable',
      504: 'Gateway Timeout'
    };
    
    return statusCodes[statusCode] || 'Unknown Status';
  };

  // Legacy function for backward compatibility
  const showValidationDetailsModal = (redirect) => {
    // Show both DNS/KV and URL validation details
    if (!redirect.lastValidation) {
      showInfoModal('Validation Status', 'This redirect has not been validated yet.');
      return;
    }

    const { lastValidation } = redirect;
    const timestamp = lastValidation.timestamp 
      ? new Date(lastValidation.timestamp).toLocaleString() 
      : 'Unknown';
    
    let message = `Last validated: ${timestamp}\n\n`;

    if (lastValidation.error) {
      message += `Error: ${lastValidation.error}\n`;
      showInfoModal('Validation Error', message);
      return;
    }

    // Show combined validation details (for backward compatibility)
    showInfoModal('Validation Details', 'Please use the DNS or URL buttons for detailed validation information.');
  };


  // Helper function to display validation status with two separate buttons
  const getValidationStatus = (redirect) => {
    if (!redirect.lastValidation) {
      return (
        <div className="validation-buttons">
          <span className="validation-status not-validated">Not Validated</span>
        </div>
      );
    }

    if (redirect.lastValidation.error) {
      return (
        <div className="validation-buttons">
          <span 
            className="validation-status error clickable" 
            title="Click to view validation details"
            onClick={() => showValidationDetailsModal(redirect)}
          >
            Error
          </span>
        </div>
      );
    }

    // Get details for DNS/KV validation
    const details = redirect.lastValidation.details || {};
    const dnsKvValid = 
      (!details.googleDns || details.googleDns.googleDnsValid) && 
      (!details.acmeChallenge || details.acmeChallenge.acmeChallengeValid) && 
      (!details.cloudflareKv || details.cloudflareKv.cloudflareKvValid) && 
      (!details.cloudflareDns || details.cloudflareDns.cloudflareDnsValid);
    
    // Get details for URL redirection validation
    const urlValid = details.urlRedirection ? details.urlRedirection.urlRedirectionValid : null;

    return (
      <div className="validation-buttons">
        <button 
          className={`validation-btn dns-kv ${dnsKvValid ? 'valid' : 'invalid'}`}
          title="Click to view DNS and KV validation details"
          onClick={() => showDnsKvValidationModal(redirect)}
        >
          DNS
        </button>
        <button 
          className={`validation-btn url ${urlValid === true ? 'valid' : urlValid === false ? 'invalid' : ''}`}
          title="Click to view URL redirection validation details"
          onClick={() => showUrlValidationModal(redirect)}
        >
          URL
        </button>
      </div>
    );
  };
  
  return (
    <div className="redirect-manager">
      <div className="redirect-manager-top-bar">
        <div className="redirect-manager-actions">
          <button
            onClick={() => {
              setEditingRedirect(null);
              setIsAddingNew(true);
            }}
            disabled={isAddingNew || editingRedirect || loading}
            className="action-button"
          >
            Add New Redirect
          </button>
          <button
            onClick={() => setShowCsvUpload(true)}
            className="action-button"
            disabled={loading || isAddingNew || editingRedirect}
          >
            Upload CSV
          </button>
          <button
            onClick={handleValidateAll}
            className="action-button"
            disabled={loading}
          >
            Validate All
          </button>
          {selectedRedirectIds.length > 0 && (
            <>
              <button
                onClick={handleBulkActivate}
                className="action-button"
                style={{backgroundColor: '#28a745', color: 'white'}}
                disabled={loading}
              >
                Activate Selected ({selectedRedirectIds.length})
              </button>
              <button
                onClick={handleBulkDeactivate}
                className="action-button"
                style={{backgroundColor: '#ffc107', color: 'black'}}
                disabled={loading}
              >
                Deactivate Selected ({selectedRedirectIds.length})
              </button>
              <button
                onClick={handleValidateSelected}
                className="action-button"
                style={{backgroundColor: '#17a2b8', color: 'white'}}
                disabled={loading}
              >
                Validate Selected ({selectedRedirectIds.length})
              </button>
              {/* Only show Fix Selected button if there are fixable redirects */}
              {redirects.filter(r => selectedRedirectIds.includes(r._id) && isRedirectFixable(r)).length > 0 && (
                <button
                  onClick={handleFixSelected}
                  className="action-button"
                  style={{backgroundColor: '#0056b3', color: 'white'}}
                  disabled={loading}
                >
                  Fix Selected ({redirects.filter(r => selectedRedirectIds.includes(r._id) && isRedirectFixable(r)).length})
                </button>
              )}
              <button
                onClick={handleBulkDelete}
                className="action-button delete"
                disabled={loading}
              >
                Delete Selected ({selectedRedirectIds.length})
              </button>
            </>
          )}
        </div>
        <div className="redirect-manager-search">
          <input
            type="search"
            placeholder="Search redirects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="redirect-manager-filter">
          <label htmlFor="validationFilter" className="filter-label">Validation:</label>
          <select
            id="validationFilter"
            value={validationFilter}
            onChange={(e) => {
              setValidationFilter(e.target.value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="valid">Valid</option>
            <option value="invalid">Invalid</option>
            <option value="not-validated">Not Validated</option>
          </select>
        </div>
        <div className="redirect-manager-filter">
          <label htmlFor="filterType" className="filter-label">Type:</label>
          <select
            id="filterType"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="storefront">Storefront (SF)</option>
            <option value="wordpress">WordPress (WP)</option>
            <option value="custom">Custom (C)</option>
          </select>
        </div>
      </div>

      {showCsvUpload && (
        <div className="csv-upload-section">
          <h4>Bulk Upload Redirects</h4>
          <input type="file" accept=".csv" onChange={handleFileChange} className="csv-input"/>
          <button onClick={handleCsvUpload} disabled={!selectedFile || loading} className="action-button">
            {loading && selectedFile ? 'Processing...' : 'Upload and Process CSV'}
          </button>
          <button onClick={() => setShowCsvUpload(false)} className="action-button cancel-button" style={{marginLeft: '10px'}}>
            Cancel
          </button>
          {uploadStatus && <p className={`upload-status ${uploadStatus.includes('error') || uploadStatus.includes('Failed') ? 'error' : ''}`}>{uploadStatus}</p>}
        </div>
      )}

      {error && (
        <div className="error-message" style={{
          padding: '10px',
          margin: '10px 0',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24'
        }}>
          {error}
        </div>
      )}

      {isAddingNew && (
        <RedirectForm
          onSave={handleSaveRedirect}
          onCancel={handleCancelEdit}
        />
      )}

      {editingRedirect && (
        <RedirectForm
          redirect={editingRedirect}
          onSave={handleSaveRedirect}
          onCancel={handleCancelEdit}
        />
      )}

      {loading ? (
        <div className="loading">Loading redirects...</div>
      ) : filteredRedirects.length === 0 ? (
        <div className="no-redirects">
          <p>{searchTerm ? 'No redirects match your search.' : 'No redirects found. Create your first redirect using the button above.'}</p>
        </div>
      ) : (
        <>
          <div className="redirects-list" role="grid" aria-label="Redirects List">
            {/* Header row */}
            <div className="redirects-grid-header" role="row">
              <div className="grid-cell grid-header" role="columnheader">
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={areAllCurrentRecordsSelected()}
                  disabled={currentRecords.length === 0}
                  aria-label="Select all redirects"
                />
              </div>
              <div 
                className={`grid-cell grid-header col-type sortable ${sortField === 'redirectType' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('redirectType')}
                role="columnheader"
                aria-sort={sortField === 'redirectType' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Type
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'subdomain' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('subdomain')}
                role="columnheader"
                aria-sort={sortField === 'subdomain' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Original URL
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'destinationUrl' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('destinationUrl')}
                role="columnheader"
                aria-sort={sortField === 'destinationUrl' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Destination URL
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'active' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('active')}
                role="columnheader"
                aria-sort={sortField === 'active' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Status
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'validationStatus' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('validationStatus')}
                role="columnheader"
                aria-sort={sortField === 'validationStatus' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Validation
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'createdBy' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('createdBy')}
                role="columnheader"
                aria-sort={sortField === 'createdBy' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Created By
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'createdAt' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('createdAt')}
                role="columnheader"
                aria-sort={sortField === 'createdAt' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Created At
              </div>
              <div 
                className={`grid-cell grid-header sortable ${sortField === 'lastValidatedAt' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSortClick('lastValidatedAt')}
                role="columnheader"
                aria-sort={sortField === 'lastValidatedAt' ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Last Validated
              </div>
              <div className="grid-cell grid-header" role="columnheader">Actions</div>
            </div>

            {/* Data rows */}
            {currentRecords.map((redirect) => (
              <div 
                key={redirect._id} 
                className={`redirects-grid-row ${!redirect.active ? 'inactive' : ''} ${selectedRedirectIds.includes(redirect._id) ? 'selected-row' : ''}`}
                role="row"
              >
                <div className="grid-cell" role="gridcell" data-label="Select">
                  <input
                    type="checkbox"
                    checked={selectedRedirectIds.includes(redirect._id)}
                    onChange={() => handleSelectRow(redirect._id)}
                    aria-label={`Select ${redirect.subdomain}`}
                  />
                </div>
                <div className="grid-cell col-type" role="gridcell" data-label="Type">
                  <span className="redirect-type-symbol">
                    {getRedirectTypeSymbol(redirect.redirectType)}
                  </span>
                </div>
                <div className="grid-cell" role="gridcell" data-label="Original URL">
                  <a
                    href={`https://${redirect.subdomain}.getsauce.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {redirect.subdomain}.getsauce.com
                  </a>
                </div>
                <div className="grid-cell" role="gridcell" data-label="Destination URL">
                  <a href={redirect.destinationUrl} target="_blank" rel="noopener noreferrer">
                    {redirect.destinationUrl}
                  </a>
                </div>
                <div className="grid-cell" role="gridcell" data-label="Status">{redirect.active ? 'Active' : 'Inactive'}</div>
                <div className="grid-cell" role="gridcell" data-label="Validation">{getValidationStatus(redirect)}</div>
                <div className="grid-cell" role="gridcell" data-label="Created By">{redirect.createdBy}</div>
                <div className="grid-cell" role="gridcell" data-label="Created At">{new Date(redirect.createdAt).toLocaleString()}</div>
                <div className="grid-cell" role="gridcell" data-label="Last Validated">{redirect.lastValidatedAt ? new Date(redirect.lastValidatedAt).toLocaleString() : 'Never'}</div>
                <div className="grid-cell" role="gridcell" data-label="Actions">
                  <ActionMenu
                    actions={[
                      {
                        label: 'Edit',
                        onClick: () => setEditingRedirect(redirect),
                        disabled: loading
                      },
                      {
                        label: 'Validate',
                        onClick: () => handleValidateRedirect(redirect._id),
                        disabled: loading,
                        className: 'validate'
                      },
                      ...(isRedirectFixable(redirect) ? [{
                        label: 'Fix',
                        onClick: () => handleFixRedirect(redirect._id),
                        disabled: loading,
                        className: 'fix'
                      }] : []),
                      {
                        label: 'Delete',
                        onClick: () => handleDeleteRedirect(redirect._id),
                        disabled: loading,
                        className: 'delete'
                      }
                    ]}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="pagination-controls">
            <span>Total Records: {filteredRedirects.length}</span>
            {totalPages > 1 && (
              <div>
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                  Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal component */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        confirmText={modalConfirmText}
        cancelText={modalCancelText}
        onConfirm={modalConfirmAction}
        type={modalType}
      />
    </div>
  );
};

export default CombinedRedirectManager;